import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { parseSearchParams, ParseSearchParamsConfig } from '../util/url';

/**
 * Reads and writes URL query params for a fixed set of param names.
 *
 * Returns `[queries, setQueries]`:
 * - `queries` — current values for each param (falls back to `defaultValue` when absent from the URL)
 * - `setQueries` — stable function (safe to include in `useEffect` deps) that updates one or more
 *   params atomically. Pass `null` to remove a param from the URL.
 *
 * Stability: `setQueries` is wrapped in `useCallback` and reads `search`/`params` through refs,
 * so its identity never changes across renders. This means effects that call `setQueries` can
 * list it as a dep without causing infinite loops.
 */
export const useQueriesControl = <const P extends readonly string[], C extends ParseSearchParamsConfig<P>>(params: P, config: Partial<C> = {}) => {
	const [search, setSearch] = useSearchParams();

	const queries = parseSearchParams(search, params, config);

	const searchRef = useRef(search);
	searchRef.current = search;

	const paramsRef = useRef(params);
	paramsRef.current = params;

	const onChange = useCallback(
		<U extends Partial<{ [K in P[number]]: string | number | boolean | null }>>(value: U) => {
			const current = searchRef.current;
			const currentParams = paramsRef.current;
			Object.entries(value).forEach(([key, val]) => {
				if (!currentParams.includes(key as any)) {
					return;
				}

				if (val === null || val === undefined) {
					current.delete(key);
				} else if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
					current.set(key, val.toString());
				}
			});

			setSearch(current);
		},
		[setSearch]
	);

	return [queries, onChange] as const;
};

// Common transformers
export const positiveInteger = (page: string | undefined) => {
	if (!page) {
		return 1;
	}

	const parsed = parseInt(page, 10);
	return isNaN(parsed) ? 1 : Math.max(1, parsed);
};
